import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import TourTooltip from './components/TourTooltip';
import WelcomeCard from './components/WelcomeCard';
import CompletionCard from './components/CompletionCard';
import { taskService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';

const OnboardingInteractiveTour = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tourState, setTourState] = useState('welcome'); // 'welcome', 'tour', 'completed'
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [sampleTasksImported, setSampleTasksImported] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const sampleTasks = [
    {
      title: 'Complete quarterly report presentation',
      notes: 'Prepare slides and data analysis for Q1 review meeting with stakeholders',
      dueDate: new Date(Date.now() + 86400000)?.toISOString()?.split('T')?.[0],
      dueTime: '14:00',
      estimatedTime: '3h',
      tags: ['Work', 'Urgent'],
      quadrantId: 'urgent-important',
      completed: false,
      isUrgent: true,
      isImportant: true,
      recurrence: 'none'
    },
    {
      title: 'Review and update project roadmap',
      notes: 'Align team goals with company objectives for next quarter',
      dueDate: new Date(Date.now() + 259200000)?.toISOString()?.split('T')?.[0],
      dueTime: '10:00',
      estimatedTime: '2h',
      tags: ['Planning', 'Strategy'],
      quadrantId: 'not-urgent-important',
      completed: false,
      isUrgent: false,
      isImportant: true,
      recurrence: 'monthly'
    },
    {
      title: 'Respond to client emails',
      notes: 'Address pending inquiries from three different clients',
      dueDate: new Date(Date.now() + 43200000)?.toISOString()?.split('T')?.[0],
      dueTime: '16:00',
      estimatedTime: '1h',
      tags: ['Communication'],
      quadrantId: 'urgent-not-important',
      completed: false,
      isUrgent: true,
      isImportant: false,
      recurrence: 'daily'
    },
    {
      title: 'Organize digital files and folders',
      notes: 'Clean up desktop and archive old project files',
      dueDate: new Date(Date.now() + 604800000)?.toISOString()?.split('T')?.[0],
      dueTime: '',
      estimatedTime: '1h 30m',
      tags: ['Organization'],
      quadrantId: 'not-urgent-not-important',
      completed: false,
      isUrgent: false,
      isImportant: false,
      recurrence: 'weekly'
    }
  ];

  const tourSteps = [
    {
      target: '.tour-quadrant-grid',
      content: 'This is the Eisenhower Matrix - a powerful tool to prioritize your tasks. It has 4 quadrants based on urgency and importance.',
      placement: 'center',
      disableBeacon: true,
      title: 'Welcome to the Eisenhower Matrix'
    },
    {
      target: '.tour-urgent-important',
      content: 'Urgent & Important tasks require immediate attention. These are your top priorities - do them first!',
      placement: 'bottom',
      title: 'Do First Quadrant'
    },
    {
      target: '.tour-not-urgent-important',
      content: 'Not Urgent but Important tasks are for long-term success. Schedule dedicated time for these.',
      placement: 'bottom',
      title: 'Schedule Quadrant'
    },
    {
      target: '.tour-urgent-not-important',
      content: 'Urgent but Not Important tasks can often be delegated. Consider if someone else can handle them.',
      placement: 'top',
      title: 'Delegate Quadrant'
    },
    {
      target: '.tour-not-urgent-not-important',
      content: 'Not Urgent & Not Important tasks should be eliminated or minimized. Focus your energy elsewhere.',
      placement: 'top',
      title: 'Eliminate Quadrant'
    },
    {
      target: '.tour-add-task-button',
      content: 'Click the "Add task" button in any quadrant to create a new task. You can also use the floating action button.',
      placement: 'left',
      title: 'Creating Tasks'
    },
    {
      target: '.tour-task-card',
      content: 'Tasks can be dragged and dropped between quadrants. Try moving a task to reorganize your priorities!',
      placement: 'right',
      title: 'Drag & Drop Tasks'
    },
    {
      target: '.tour-search-bar',
      content: 'Use the search bar to quickly find tasks. You can search by title, notes, or tags.',
      placement: 'bottom',
      title: 'Search Tasks'
    },
    {
      target: '.tour-filter-button',
      content: 'Click here to access filters and sorting options. Filter by status, due date, or custom criteria.',
      placement: 'bottom',
      title: 'Filter & Sort'
    },
    {
      target: '.tour-bulk-mode-button',
      content: 'Enable bulk mode to select multiple tasks at once for batch operations like delete or move.',
      placement: 'bottom',
      title: 'Bulk Actions'
    }
  ];

  const handleJoyrideCallback = (data) => {
    const { status, action, index, type } = data;

    if ([EVENTS?.STEP_AFTER, EVENTS?.TARGET_NOT_FOUND]?.includes(type)) {
      setStepIndex(index + (action === ACTIONS?.PREV ? -1 : 1));
    }

    if ([STATUS?.FINISHED, STATUS?.SKIPPED]?.includes(status)) {
      setRun(false);
      setTourState('completed');
    }
  };

  const handleStartTour = () => {
    setTourState('tour');
    setRun(true);
  };

  const handleSkipTour = () => {
    navigate('/main-dashboard');
  };

  const handleImportSampleTasks = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    setIsImporting(true);
    try {
      for (const task of sampleTasks) {
        await taskService?.createTask(task);
      }
      setSampleTasksImported(true);
    } catch (error) {
      console.error('Error importing sample tasks:', error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/main-dashboard');
  };

  if (tourState === 'welcome') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8 md:py-12 lg:py-16">
          <WelcomeCard
            onStartTour={handleStartTour}
            onSkipTour={handleSkipTour}
            onImportSampleTasks={handleImportSampleTasks}
            sampleTasksImported={sampleTasksImported}
            isImporting={isImporting}
          />
        </div>
      </div>
    );
  }

  if (tourState === 'completed') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8 md:py-12 lg:py-16">
          <CompletionCard
            onGoToDashboard={handleGoToDashboard}
            sampleTasksImported={sampleTasksImported}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Tour Demo Dashboard */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
              Interactive Tour
            </h1>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSkipTour}
              iconName="X"
            >
              Skip Tour
            </Button>
          </div>
          <p className="text-muted-foreground">
            Follow the guided tour to learn how to use the Eisenhower Matrix
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-card rounded-xl border border-border p-4 md:p-5 mb-4 tour-search-bar">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="flex-1">
              <div className="relative">
                <Icon
                  name="Search"
                  size={18}
                  color="var(--color-muted-foreground)"
                  className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  className="w-full pl-10 pr-4 py-2.5 md:py-3 rounded-lg border border-border bg-background text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  disabled
                />
              </div>
            </div>
            <button
              className="w-10 h-10 md:w-11 md:h-11 rounded-lg flex items-center justify-center bg-muted text-muted-foreground tour-filter-button"
              disabled
            >
              <Icon name="SlidersHorizontal" size={20} color="currentColor" />
            </button>
            <button
              className="w-10 h-10 md:w-11 md:h-11 rounded-lg flex items-center justify-center bg-muted text-muted-foreground tour-bulk-mode-button"
              disabled
            >
              <Icon name="CheckSquare" size={20} color="currentColor" />
            </button>
          </div>
        </div>

        {/* Quadrant Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6 tour-quadrant-grid">
          {/* Urgent & Important */}
          <div className="rounded-xl border-2 border-border bg-card tour-urgent-important">
            <div className="p-4 md:p-5 border-b border-border">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#B85C5C20' }}
                  >
                    <Icon name="AlertCircle" size={20} color="#B85C5C" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-foreground">
                      Urgent & Important
                    </h3>
                    <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                      1 task
                    </p>
                  </div>
                </div>
              </div>
              <button
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all duration-250 group tour-add-task-button"
                disabled
              >
                <Icon
                  name="Plus"
                  size={16}
                  color="var(--color-muted-foreground)"
                  className="group-hover:text-primary transition-colors"
                />
                <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                  Add task
                </span>
              </button>
            </div>
            <div className="p-3 md:p-4 min-h-[200px] space-y-2">
              <div className="bg-background rounded-lg border border-border p-3 md:p-4 shadow-sm tour-task-card">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h4 className="text-sm md:text-base font-medium text-foreground flex-1">
                    Complete quarterly report
                  </h4>
                  <Icon name="GripVertical" size={16} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mb-3">
                  Prepare slides and data analysis
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Calendar" size={14} color="currentColor" />
                  <span>Today, 2:00 PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Not Urgent & Important */}
          <div className="rounded-xl border-2 border-border bg-card tour-not-urgent-important">
            <div className="p-4 md:p-5 border-b border-border">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#7BA05B20' }}
                  >
                    <Icon name="Calendar" size={20} color="#7BA05B" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-foreground">
                      Not Urgent & Important
                    </h3>
                    <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                      0 tasks
                    </p>
                  </div>
                </div>
              </div>
              <button
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-border"
                disabled
              >
                <Icon name="Plus" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">Add task</span>
              </button>
            </div>
            <div className="p-3 md:p-4 min-h-[200px]">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: '#7BA05B10' }}
                >
                  <Icon name="Calendar" size={24} color="#7BA05B" />
                </div>
                <p className="text-sm text-muted-foreground">No tasks yet</p>
              </div>
            </div>
          </div>

          {/* Urgent & Not Important */}
          <div className="rounded-xl border-2 border-border bg-card tour-urgent-not-important">
            <div className="p-4 md:p-5 border-b border-border">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#C4935A20' }}
                  >
                    <Icon name="Users" size={20} color="#C4935A" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-foreground">
                      Urgent & Not Important
                    </h3>
                    <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                      0 tasks
                    </p>
                  </div>
                </div>
              </div>
              <button
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-border"
                disabled
              >
                <Icon name="Plus" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">Add task</span>
              </button>
            </div>
            <div className="p-3 md:p-4 min-h-[200px]">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: '#C4935A10' }}
                >
                  <Icon name="Users" size={24} color="#C4935A" />
                </div>
                <p className="text-sm text-muted-foreground">No tasks yet</p>
              </div>
            </div>
          </div>

          {/* Not Urgent & Not Important */}
          <div className="rounded-xl border-2 border-border bg-card tour-not-urgent-not-important">
            <div className="p-4 md:p-5 border-b border-border">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#5A6B5A20' }}
                  >
                    <Icon name="Trash2" size={20} color="#5A6B5A" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-foreground">
                      Not Urgent & Not Important
                    </h3>
                    <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                      0 tasks
                    </p>
                  </div>
                </div>
              </div>
              <button
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-border"
                disabled
              >
                <Icon name="Plus" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">Add task</span>
              </button>
            </div>
            <div className="p-3 md:p-4 min-h-[200px]">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: '#5A6B5A10' }}
                >
                  <Icon name="Trash2" size={24} color="#5A6B5A" />
                </div>
                <p className="text-sm text-muted-foreground">No tasks yet</p>
              </div>
            </div>
          </div>
        </div>

        {/* Import Sample Tasks Button */}
        {!sampleTasksImported && (
          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              size="lg"
              onClick={handleImportSampleTasks}
              loading={isImporting}
              iconName="Download"
            >
              Import Sample Tasks
            </Button>
          </div>
        )}
      </div>

      {/* Joyride Tour */}
      <Joyride
        steps={tourSteps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        tooltipComponent={TourTooltip}
        styles={{
          options: {
            primaryColor: 'var(--color-primary)',
            zIndex: 10000,
          },
        }}
      />
    </div>
  );
};

export default OnboardingInteractiveTour;